import { Link, useParams, Navigate } from "react-router-dom";

import { Nav, Footer, Reveal } from "@/components/site";
import { getTea, teas } from "@/data/teas";
import { useEffect } from "react";

export default function TeaDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  
  if (!slug) {
    return <Navigate to="/" replace />;
  }
  
  const tea = getTea(slug);
  
  useEffect(() => {
    if (tea) {
      document.title = `${tea.name} — Mistvale Tea`;
    }
  }, [tea]);
  
  if (!tea) {
    return <Navigate to="/404" replace />;
  }
  const others = teas.filter((t) => t.slug !== tea.slug).slice(0, 3);

  return (
    <main className="bg-ivory text-forest">
      <Nav />

      {/* header */}
      <section className="bg-forest pb-24 pt-40 text-ivory md:pb-32 md:pt-52">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 md:grid-cols-12 md:px-14">
          <Reveal className="md:col-span-5">
            <div className="overflow-hidden">
              <img
                src={tea.image}
                alt={tea.name}
                width={1200}
                height={1500}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={150} className="md:col-span-6 md:col-start-7 md:self-center">
            <div className="eyebrow mb-6 text-gold">— {tea.kind}</div>
            <h1 className="display-serif text-5xl md:text-7xl">{tea.name}</h1>
            <p className="mt-4 font-serif text-xl italic text-gold/90">{tea.notes}</p>
            <p className="mt-8 max-w-lg font-light leading-relaxed text-ivory/80">{tea.body}</p>
            <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-ivory/55">{tea.origin}</div>
            <div className="mt-10 flex flex-wrap gap-8">
              <Link
                to={`/?tea=${tea.slug}&intent=samples#enquiry`}
                className="border-b border-gold pb-2 text-[11px] uppercase tracking-[0.28em] text-gold transition hover:border-ivory hover:text-ivory"
              >
                Request a Sample Tin →
              </Link>
              <Link
                to={`/?tea=${tea.slug}&intent=price-list#enquiry`}
                className="border-b border-ivory/40 pb-2 text-[11px] uppercase tracking-[0.28em] text-ivory/80 transition hover:border-gold hover:text-gold"
              >
                Trade Price List →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* story + brewing */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-14 md:py-32">
        <div className="grid gap-16 md:grid-cols-12">
          <Reveal className="md:col-span-6">
            <div className="eyebrow mb-6 text-moss">— The Story</div>
            <h2 className="display-serif text-4xl text-forest md:text-5xl">How it is made.</h2>
            <p className="mt-8 font-light leading-relaxed text-forest/80">{tea.story}</p>
            <div className="mt-10">
              <div className="eyebrow mb-4 text-moss">Available grades</div>
              <ul className="space-y-2">
                {tea.grades.map((g) => (
                  <li key={g} className="font-serif text-lg text-forest">— {g}</li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={150} className="md:col-span-5 md:col-start-8">
            <div className="border border-forest/15 bg-cream p-8 md:p-10">
              <div className="eyebrow mb-6 text-gold">— Brewing Notes</div>
              <dl className="divide-y divide-forest/10">
                {[
                  ["Leaf", tea.brewing.leaf],
                  ["Water", tea.brewing.water],
                  ["Time", tea.brewing.time],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[5rem_1fr] items-baseline gap-6 py-4">
                    <dt className="text-[10px] uppercase tracking-[0.22em] text-forest/55">{k}</dt>
                    <dd className="font-serif text-lg text-forest">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 font-serif italic leading-relaxed text-forest/70">{tea.brewing.note}</p>
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.22em] text-forest/50">
              Every tin carries a batch code —{" "}
              <a href="/#trace" className="border-b border-gold/50 text-forest transition hover:text-gold">
                trace it here
              </a>
            </p>
          </Reveal>
        </div>
      </section>

      {/* more from the house */}
      <section className="bg-cream py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-14">
          <Reveal>
            <div className="flex items-end justify-between border-b border-forest/15 pb-8">
              <h2 className="display-serif text-4xl text-forest md:text-5xl">More from the house.</h2>
              <a
                href="/#ranges"
                className="hidden text-[11px] uppercase tracking-[0.28em] text-forest/70 transition hover:text-gold md:block"
              >
                All Ranges →
              </a>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-12 md:grid-cols-3 md:gap-8">
            {others.map((t, i) => (
              <Reveal key={t.slug} delay={i * 120}>
                <Link to={`/tea/${t.slug}`} className="group block">
                  <div className="overflow-hidden bg-forest/5">
                    <img
                      src={t.image}
                      alt={t.name}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-5 text-[10px] uppercase tracking-[0.22em] text-forest/55">{t.kind}</div>
                  <h3 className="mt-2 font-serif text-2xl text-forest transition-colors group-hover:text-gold">
                    {t.name}
                  </h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
